// Makao online (owner 2026-10-29): a store with the offline store's interface (makao/store.ts), fed by the server's
// frames instead of the local bots, so MakaoTable.tsx and the end window work unchanged. The player's taps go to the
// server as moves; what the table shows is always the server's state (seat 0 = me).
import { canAdd, choiceNeeded, type Choice, type HCard, type MakaoState } from '../makao/rules';
import { DEAL_MS, SAID_MS, makaoStore, setMakaoStore, type Happened, type MakaoStore, type TableState } from '../makao/store';
import { SLOW } from '../hearts/store';
import { HIDDEN, expand, seatNamesFor, clockOf, type CardEndFrame, type CardStart, type CardStateFrame, type OnlineCardGame, type OnlineDeps } from './common';
import { renamed } from './common';

/** The server's turned view as the rules' state (the hidden cards as face-down stand-ins, no pattern). */
function toState(v: Record<string, unknown>): MakaoState {
  return {
    ...(v as unknown as MakaoState),
    hands: (v.hands as unknown[]).map(expand),
    stock: Array.from({ length: Number(v.stock) || 0 }, () => HIDDEN),
    pattern: [],
  };
}

export function createOnlineMakao(deps: OnlineDeps): OnlineCardGame & { store: MakaoStore } {
  const empty: TableState = { game: null, seconds: 0, deal: 0, dealing: false, choosing: null, said: null, last: null, laidByPlayer: 0, demo: false, names: null, startedAt: 0, endedAt: null, deadlineAt: null };
  let state: TableState = empty;
  const listeners = new Set<() => void>();
  let clock: ReturnType<typeof setInterval> | null = null;
  let dealTimer: ReturnType<typeof setTimeout> | null = null;
  let saidTimer: ReturnType<typeof setTimeout> | null = null;
  let offline: MakaoStore | null = null;
  const set = (patch: Partial<TableState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const stopTimers = () => {
    if (clock !== null) clearInterval(clock);
    if (dealTimer !== null) clearTimeout(dealTimer);
    if (saidTimer !== null) clearTimeout(saidTimer);
    clock = dealTimer = saidTimer = null;
  };
  /** A move of mine is on its way: no other until the server's answer (a slow line showed the old table meanwhile,
   *  and a second tap went out late). */
  let waiting = false;
  /** My move, when it is mine and the deal is over. */
  const mine = () => {
    const g = state.game;
    return g && g.phase === 'play' && g.turn === 0 && !state.dealing && !waiting ? g : null;
  };
  const move = (m: Record<string, unknown>) => {
    deps.onStep?.(`makao online: ${String(m.type)}`);
    waiting = true;
    deps.send(m);
  };

  const store: MakaoStore = {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame() {
      // online the server deals
    },
    lay(card: HCard) {
      if (mine() && !state.choosing) move({ type: 'lay', card: { s: card.s, r: card.r } });
    },
    finish() {
      const g = mine();
      if (!g || !g.laid.length || state.choosing) return;
      const ask = choiceNeeded(g);
      if (ask) set({ choosing: ask });
      else move({ type: 'finish' });
    },
    choose(choice: Choice) {
      if (mine() && state.choosing) move({ type: 'finish', choice });
    },
    draw() {
      if (mine() && !state.choosing) move({ type: 'draw' });
    },
    pass() {
      if (mine()) move({ type: 'pass' });
    },
    stop: stopTimers,
    load() {
      // e2e only, offline
    },
  };

  return {
    game: 'makao',
    store,
    begin(start: CardStart) {
      stopTimers();
      offline ??= makaoStore();
      state = { ...empty, deal: state.deal, names: seatNamesFor(start), ...clockOf(start) };
      setMakaoStore(store);
      clock = setInterval(() => {
        const g = state.game;
        if (g && g.phase === 'play' && !state.dealing) set({ seconds: state.seconds + 1 });
      }, 1000);
    },
    state(f: CardStateFrame) {
      waiting = false;
      const g = toState(f.state);
      const before = state.game;
      const deadlineAt = f.deadline !== undefined ? Date.now() + f.deadline : null;
      const last = (f.last ? { seat: f.last.seat, what: f.last.what, n: Number(f.last.n ?? 1) } : state.last) as Happened | null;
      if (!before) {
        // the deal: shown at once (the view throws the cards out), the server waits for it too
        set({ game: g, deal: state.deal + 1, dealing: true, choosing: null, said: null, last: null, laidByPlayer: 0, deadlineAt });
        dealTimer = setTimeout(() => set({ dealing: false }), DEAL_MS * SLOW);
        return;
      }
      const said = g.phase === 'play' ? [0, 1, 2, 3].find((p) => g.hands[p]!.length === 1 && before.hands[p]!.length > 1) : undefined;
      // my turn with cards laid and none of the rank left: a jack or an ace asks first (the server ends the turn
      // by itself when nothing is to be chosen)
      const choosing = g.phase === 'play' && g.turn === 0 && g.laid.length && !canAdd(g) ? choiceNeeded(g) : null;
      const laidByPlayer = state.laidByPlayer + (f.last && f.last.seat === 0 && f.last.what === 'lay' ? 1 : 0);
      set({ game: g, last, choosing, laidByPlayer, deadlineAt, ...(g.phase === 'over' ? { endedAt: Date.now() } : {}) });
      if (said !== undefined) {
        set({ said: { seat: said, n: (state.said?.n ?? 0) + 1 } });
        if (saidTimer !== null) clearTimeout(saidTimer);
        saidTimer = setTimeout(() => set({ said: null }), SAID_MS * SLOW);
      }
    },
    end(f: CardEndFrame) {
      const g = state.game;
      if (g) set({ game: { ...g, phase: 'over', winner: f.winners[0] ?? -1, turn: -1 }, endedAt: Date.now(), deadlineAt: null });
      if (clock !== null) clearInterval(clock);
      clock = null;
      deps.openWindow('makao_end');
    },
    rename(seat, name, bot, flag) {
      set({ names: renamed(state.names, seat, name, bot, flag) });
    },
    stop() {
      stopTimers();
      deps.closeWindow('makao_end');
      if (offline) setMakaoStore(offline);
      state = { ...empty, deal: state.deal };
    },
  };
}
